import { AppConstants } from "@app/app.constants";
import { FindingReportDataService } from "@app/findingreport/services";
import { KriDataService } from "@app/kri/services/kri-data.service";
import { ReportDataService } from "@app/report/services/ReportDataService";
import { RiskDataService } from "@app/risk/services/RiskDataService";
import { GenericListFieldType } from "@app/shared/models/GenericList/GenericListField";
import { StandingdataDataService } from "@app/standingdata/shared/standingdata.service";
import { ModuleSubType } from "@enums/ModuleSubType";
import { ModuleType } from "@enums/ModuleType";
import { StandingDataType } from "@enums/StandingDataType";
import { IdNameCombination } from "@models/generic/IdNameCombination";
import { WorkspacePresetService } from "@app/shared/services/workspace/workspace-preset.service";
import { Observable, of } from "rxjs";
import { map } from "rxjs/operators";
import { CalendarWidgetComponent } from "../widgets/calendar-widget/calendar-widget.component";
import { EmbeddedReportWidgetComponent } from "../widgets/embedded-report-widget/embedded-report-widget.component";
import { BusinessAssessmentWidgetComponent } from "../widgets/highchart-widgets/types/business-assessment-widget.component";
import { ControlEffTestingWidgetComponent } from "../widgets/highchart-widgets/types/controls-eff-testing-widget.component";
import { ControlSimpleEffOverdueWidgetComponent } from "../widgets/highchart-widgets/types/controls-simple-eff-overdue-widget.component";
import { DesignImplementationWidgetComponent } from "../widgets/highchart-widgets/types/designimplementation-widget-component";
import { EffectivenessOverdueWidgetComponent } from "../widgets/highchart-widgets/types/effectiveness-overdue-widget.component";
import { EventWidgetComponent } from "../widgets/highchart-widgets/types/event-widget.component";
import { KriWidgetComponent } from "../widgets/highchart-widgets/types/kri-widget.component";
import { MoiWidgetComponent } from "../widgets/highchart-widgets/types/moi-widget.component";
import { ThirdPartyWidgetComponent } from "../widgets/highchart-widgets/types/thirdparty-widget.component";
import { ProcessWidgetComponent } from "../widgets/process-widget/process-widget.component";
import { VideoWidgetComponent } from "../widgets/video-widget/video-widget.component";
import { FormsDataService } from "./../../forms/services/forms-data.service";
import { FormOverviewWidgetComponent } from "./../widgets/form-overview-widget/form-overview-widget.component";
import { FindingReportWidgetComponent } from "./../widgets/highchart-widgets/types/finding-report-widget.component";
import { RisksPerCatalogueWidgetComponent } from "./../widgets/highchart-widgets/types/risks-per-catalogue-widget.component";
import { KriDetailWidgetComponent } from "./../widgets/kri-detail-widget/kri-detail-widget.component";
import { LinkWidgetComponent } from "./../widgets/link-widget/link-widget.component";
import { RiskMatrixWidgetComponent } from "./../widgets/risk-matrix-widget/risk-matrix-widget.component";
import { TextWidgetComponent } from "./../widgets/text-widget/text-widget.component";
import { DashboardGraphType } from "./enums/DashboardGraphType.enum";
import { DashboardLegendType } from "./enums/DashboardLegendType.enum";
import { DashboardWidgetType } from "./enums/DashboardWidgetType.enum";
import { KriWidgetTypeDescription } from "./enums/KriWidgetType.enum";
import { ProcessWidgetLink, ProcessWidgetLinkDescription } from "./enums/ProcessWidgetLink.enum";
import { QuickLinkItemContent } from "./enums/QuickLinkItemContent.enum";
import { WidgetConfigType } from "./enums/WidgetConfigType.enum";
import { DashboardWidgetModel } from "./models/DashboardWidgetModel";
import { WidgetConfigField } from "./models/WidgetConfigField";
import { WidgetFilterKey } from "./models/WidgetFilterKey";
import { getMoiTypes } from "@enums/moi/MoiTypes";
import { ModuleDetailWidgetComponent } from "../widgets/module-detail-widget/module-detail-widget.component";
import { WorkspaceCountService } from "@app/shared/services/workspace/workspace-count.service";
import { IncidentWidgetComponent } from "../widgets/highchart-widgets/types/incident-widget.component";
import { IncidentMapbackService } from "@app/incident/services/incident-mapback.service";
import { FormStructureType } from "@app/forms/enums/FormStructureType";
import { from } from "rxjs";
import { ApplicationSettings } from "@services/http/settings/application-settings";
import { SettingsDataService } from "@services/http/SettingsDataService";
import { PresetService } from "@app/preset/services/preset.service";
import { IncidentStatusService } from "@app/incident/services/incident-status.service";

export class DashboardWidgetMapping {
    private static mapping: { [type in DashboardWidgetType]: DashboardWidgetModel };

    private static incidentModuleString: string = "Incidents";
    public static all() {
        if (!this.mapping) {
            this.set();
        }

        return this.asCopy(Object.values(this.mapping));
    }

    public static get(type: DashboardWidgetType) {
        if (!this.mapping) {
            this.set();
        }

        return this.asCopy(this.mapping[type], ["component", "configurations"]);
    }

    private static asCopy<T>(original: T, persistantKeys?: (keyof T)[]): T {
        const copy = JSON.parse(JSON.stringify(original)) as T;

        if (persistantKeys) {
            persistantKeys.forEach((key) => {
                copy[key] = original[key];
            });
        }

        return copy;
    }

    private static set() {
        this.mapping = {
            "10": {
                component: LinkWidgetComponent,
                module: ModuleType.COMMON,
                title: "dashboard.widgetCatalog.moduleOverviewTitle",
                description: "dashboard.widgetCatalog.moduleOverviewDescription",
                icon: "fad fa-table",
                right: "HasWorkspacesAccess",
                showLoader: true,
                minCols: 2,
                minRows: 2,
                customConfig: {
                    type: DashboardWidgetType.ModuleOverview,
                    globalFilterEnabled: false,
                },
                configurations: [...DashboardWidgetMapping._defaultQuickLinkFilters],
            },
            "20": {
                component: ModuleDetailWidgetComponent,
                module: ModuleType.COMMON,
                title: "dashboard.widgetCatalog.moduleCountTitle",
                description: "dashboard.widgetCatalog.moduleCountDescription",
                icon: "fad fa-tally",
                showLoader: true,
                right: "HasWidgetCountAccess",
                minCols: 1,
                minRows: 1,

                hasGlobalOpen: true,

                customConfig: {
                    type: DashboardWidgetType.ModuleCount,
                    hideHeader: true,
                    globalFilterEnabled: true,
                },
                configurations: [
                    {
                        prettyName: "dashboard.widgetCatalog.moduleCountCustomTitleLabel",
                        description: "dashboard.widgetCatalog.moduleCountCustomTitleDescription",
                        fieldName: WidgetFilterKey.ModuleDetail.CustomTitle,
                        fieldType: GenericListFieldType.Text,
                        configType: WidgetConfigType.WidgetConfig,
                        defaultValue: "",
                    },
                    {
                        prettyName: "dashboard.widgetCatalog.moduleCountModuleLabel",
                        fieldName: WidgetFilterKey.ModuleDetail.Module,
                        fieldType: GenericListFieldType.SingleSelect,
                        configType: WidgetConfigType.DataFilter,
                        defaultValue: "",
                        getDataMethod: () => {
                            const countService =
                                AppConstants.AppInjector.get(WorkspaceCountService);
                            const settingsService =
                                AppConstants.AppInjector.get(SettingsDataService);
                            const useIncidents$ = settingsService
                                .getSetting(ApplicationSettings.UseIncidents)
                                .pipe(map((setting) => setting.BoolValue));
                            let availableModules = countService.getAvailableModules().pipe(
                                map((data) => {
                                    return data.map((x) => {
                                        return { ID: x.presetCode, Name: x.name };
                                    });
                                }),
                            );
                            if (useIncidents$) {
                                // Add incidents to the list of available modules
                                availableModules = availableModules.pipe(
                                    map((modules) => {
                                        const index = modules.findIndex(
                                            (module) => module.ID === "LER",
                                        );
                                        const updatedModules = [...modules];
                                        if (index !== -1) {
                                            updatedModules.splice(index + 1, 0, {
                                                ID: this.incidentModuleString,
                                                Name: this.incidentModuleString,
                                            });
                                        } else {
                                            updatedModules.push({
                                                ID: this.incidentModuleString,
                                                Name: this.incidentModuleString,
                                            });
                                        }
                                        return updatedModules;
                                    }),
                                );
                            }

                            return availableModules;
                        },
                    },
                    {
                        prettyName: "dashboard.widgetCatalog.moduleCountWorkspacePresetLabel",
                        description: "dashboard.widgetCatalog.moduleCountWorkspacePresetDescription",
                        fieldName: WidgetFilterKey.ModuleDetail.WorkspacePreset,
                        fieldType: GenericListFieldType.SingleSelect,
                        configType: WidgetConfigType.DataFilter,
                        defaultValue: "",
                        getDataMethodByRow(row) {
                            const presetService = AppConstants.AppInjector.get(PresetService);
                            const settingsService =
                                AppConstants.AppInjector.get(SettingsDataService);
                            const useIncidents$ = settingsService
                                .getSetting(ApplicationSettings.UseIncidents)
                                .pipe(map((setting) => setting.BoolValue));
                            const selectedModule = row[WidgetFilterKey.ModuleDetail.Module];
                            if (!selectedModule) {
                                return of([
                                    { ID: -1, Name: "dashboard.widgetCatalog.noPresetOption" },
                                ]);
                            }
                            if (selectedModule == "Incidents" && useIncidents$) {
                                // If incidents is selected, retreive presets from incident backend
                                return presetService.getPublicPresets().pipe(
                                    map((presets) => {
                                        return presets.map((x) => {
                                            return { ID: x.id, Name: x.name };
                                        });
                                    }),
                                );
                            } else {
                                const countService =
                                    AppConstants.AppInjector.get(WorkspaceCountService);
                                return countService.getAvailableModules().pipe(
                                    map((data) => {
                                        return data.find((s) => s.presetCode == selectedModule)
                                            .presets;
                                    }),
                                );
                            }
                        },
                        checkReloadDataByRowChanges(previous, current) {
                            const keyToCheck = WidgetFilterKey.ModuleDetail.Module;
                            if (previous[keyToCheck] !== current[keyToCheck]) {
                                current[WidgetFilterKey.ModuleDetail.WorkspacePreset] = null; // Reset the selected preset if the module has changed
                                return true;
                            }

                            return false;
                        },
                    },
                ],
                globalFilterKeys: [],
            },
            "100": {
                component: LinkWidgetComponent,
                module: ModuleType.COMMON,
                title: "dashboard.widgetCatalog.insightsTitle",
                description: "dashboard.widgetCatalog.insightsDescription",
                icon: "fad fa-file-chart-line",
                right: "HasInsightsAccess",
                showLoader: true,
                minCols: 2,
                minRows: 2,
                customConfig: {
                    type: DashboardWidgetType.Insights,
                    globalFilterEnabled: false,
                },
                configurations: [...DashboardWidgetMapping._defaultQuickLinkFilters],
            },
            "110": {
                component: LinkWidgetComponent,
                module: ModuleType.COMMON,
                title: "dashboard.widgetCatalog.navigatorsTitle",
                description: "dashboard.widgetCatalog.navigatorsDescription",
                icon: "fad fa-sitemap",
                right: "HasNavigatorAccess",
                showLoader: true,
                minCols: 2,
                minRows: 2,
                customConfig: {
                    type: DashboardWidgetType.Navigators,
                    globalFilterEnabled: false,
                },
                configurations: [...DashboardWidgetMapping._defaultQuickLinkFilters],
            },
            "120": {
                component: LinkWidgetComponent,
                module: ModuleType.COMMON,
                title: "dashboard.widgetCatalog.adminPanelTitle",
                description: "dashboard.widgetCatalog.adminPanelDescription",
                icon: "fad fa-toolbox",
                right: "HasAdminAccess",
                showLoader: true,
                minCols: 2,
                minRows: 2,
                customConfig: {
                    type: DashboardWidgetType.AdminPanel,
                    globalFilterEnabled: false,
                },
                configurations: [...DashboardWidgetMapping._defaultQuickLinkFilters],
            },
            "200": {
                component: CalendarWidgetComponent,
                module: ModuleType.COMMON,
                title: "dashboard.widgetCatalog.calendarTitle",
                description: "dashboard.widgetCatalog.calendarDescription",
                icon: "fad fa-calendar-alt",
                showLoader: true,
                minCols: 2,
                minRows: 2,
                customConfig: {
                    type: DashboardWidgetType.Calendar,
                    globalFilterEnabled: false,
                },
                configurations: [
                    {
                        prettyName: "dashboard.widgetCatalog.calendarShowActionsLabel",
                        fieldName: WidgetFilterKey.Calendar.ShowActions,
                        fieldType: GenericListFieldType.CheckBox,
                        configType: WidgetConfigType.WidgetConfig,
                        defaultValue: false,
                    },
                ],
            },
            "300": {
                component: TextWidgetComponent,
                module: ModuleType.COMMON,
                title: "dashboard.widgetCatalog.textWidgetTitle",
                description: "dashboard.widgetCatalog.textWidgetDescription",
                icon: "fad fa-paragraph",
                showLoader: false,
                minCols: 1,
                minRows: 1,
                customConfig: {
                    type: DashboardWidgetType.TextWidget,
                    globalFilterEnabled: false,
                },
                configurations: [
                    {
                        prettyName: "dashboard.widgetCatalog.textWidgetEmbeddedTextLabel",
                        fieldName: WidgetFilterKey.TextWidget.Text,
                        fieldType: GenericListFieldType.WYSIWYG,
                        configType: WidgetConfigType.WidgetConfig,
                        defaultValue: "",
                    },
                ],
            },
            "310": {
                component: VideoWidgetComponent,
                module: ModuleType.COMMON,
                title: "dashboard.widgetCatalog.videoWidgetTitle",
                description: "dashboard.widgetCatalog.videoWidgetDescription",
                icon: "fas fa-video",
                showLoader: false,
                minCols: 1,
                minRows: 1,
                hasGlobalOpen: false,
                legendConfigurable: false,
                customConfig: {
                    type: DashboardWidgetType.VideoWidget,
                    globalFilterEnabled: false,
                },
                configurations: [
                    {
                        prettyName: "dashboard.widgetCatalog.videoWidgetUrlLabel",
                        description: "dashboard.widgetCatalog.videoWidgetUrlDescription",
                        fieldName: WidgetFilterKey.VideoWidget.Url,
                        fieldType: GenericListFieldType.Text,
                        configType: WidgetConfigType.DataFilter,
                        defaultValue: "",
                    },
                ],
            },
            "400": {
                component: EmbeddedReportWidgetComponent,
                module: ModuleType.COMMON,
                title: "dashboard.widgetCatalog.embeddedReportTitle",
                description: "dashboard.widgetCatalog.embeddedReportDescription",
                icon: "fad fa-file-invoice",
                right: "HasReportingAccess",
                hasGlobalOpen: true,
                showLoader: true,
                minCols: 6,
                minRows: 4,
                customConfig: {
                    type: DashboardWidgetType.EmbeddedReportWidget,
                    globalFilterEnabled: false,
                },
                configurations: [
                    {
                        prettyName: "dashboard.widgetCatalog.embeddedReportReportLabel",
                        fieldName: WidgetFilterKey.Insights.ReportToEmbed,
                        fieldType: GenericListFieldType.SingleSelect,
                        configType: WidgetConfigType.WidgetConfig,
                        defaultValue: 0,
                        getDataMethod: () => {
                            const ds = AppConstants.AppInjector.get(ReportDataService);
                            return ds.getList();
                        },
                    },
                    {
                        prettyName: "dashboard.widgetCatalog.embeddedReportLoadOnLoadLabel",
                        description: "dashboard.widgetCatalog.embeddedReportLoadOnLoadDescription",
                        fieldName: WidgetFilterKey.Insights.ReportSkipLoadCheck,
                        fieldType: GenericListFieldType.CheckBox,
                        configType: WidgetConfigType.WidgetConfig,
                        defaultValue: false,
                    },
                ],
            },
            "1010": {
                component: RiskMatrixWidgetComponent,
                module: ModuleType.ORM,
                title: "dashboard.widgetCatalog.riskMatrixTitle",
                description: "dashboard.widgetCatalog.riskMatrixDescription",
                icon: "fas fa-exclamation-triangle",
                right: "HasRiskAccess",
                showLoader: true,
                minCols: 3,
                minRows: 3,
                hasGlobalOpen: true,
                legendConfigurable: true,
                customConfig: {
                    type: DashboardWidgetType.RiskMatrix,
                    globalFilterEnabled: true,
                    legendType: DashboardLegendType.iconlabel,
                },
                configurations: [
                    {
                        prettyName: "dashboard.widgetCatalog.riskMatrixShowSummaryLabel",
                        description: "dashboard.widgetCatalog.riskMatrixShowSummaryDescription",
                        fieldName: WidgetFilterKey.RiskMatrix.ShowSummary,
                        fieldType: GenericListFieldType.CheckBox,
                        configType: WidgetConfigType.WidgetConfig,
                        defaultValue: true,
                    },
                    {
                        prettyName: "dashboard.widgetCatalog.riskMatrixNetOrGrossLabel",
                        description: "dashboard.widgetCatalog.riskMatrixNetOrGrossDescription",
                        fieldName: WidgetFilterKey.RiskMatrix.NetOrGross,
                        fieldType: GenericListFieldType.SingleSelect,
                        configType: WidgetConfigType.DataFilter,
                        defaultValue: 1,
                        getDataMethod: () => {
                            const ds = AppConstants.AppInjector.get(RiskDataService);
                            return ds.getRiskScoringTypes();
                        },
                    },
                    {
                        prettyName: "dashboard.widgetCatalog.riskMatrixImpactScaleLabel",
                        description: "dashboard.widgetCatalog.riskMatrixImpactScaleDescription",
                        fieldName: WidgetFilterKey.RiskMatrix.ImpactScale,
                        fieldType: GenericListFieldType.SingleSelect,
                        configType: WidgetConfigType.DataFilter,
                        defaultValue: 0,
                        getDataMethod: () => {
                            const ds = AppConstants.AppInjector.get(RiskDataService);
                            return ds.getAvailableScales();
                        },
                    },
                    {
                        prettyName: "dashboard.widgetCatalog.riskMatrixCatalogueLabel",
                        description: "dashboard.widgetCatalog.riskMatrixCatalogueDescription",
                        fieldName: WidgetFilterKey.RiskMatrix.RiskCatalogue,
                        fieldType: GenericListFieldType.SingleSelect,
                        configType: WidgetConfigType.DataFilter,
                        defaultValue: 0,
                        getDataMethod: () => {
                            const ds = AppConstants.AppInjector.get(StandingdataDataService);
                            return ds.getAllByType(StandingDataType.RiskCatalogue);
                        },
                    },
                    DashboardWidgetMapping._globalPeriodFilter,
                    DashboardWidgetMapping._globalOrganizationFilter.Organization,
                    DashboardWidgetMapping._globalOrganizationFilter.OrganizationIncludeChildren,
                    DashboardWidgetMapping._globalBusinessDimensionFilter.BusinessDimension,
                    DashboardWidgetMapping._globalBusinessDimensionFilter
                        .BusinessDimensionIncludeChildren,
                    DashboardWidgetMapping._globalFrameworkDimensionFilter.FrameworkDimension,
                    DashboardWidgetMapping._globalFrameworkDimensionFilter
                        .FrameworkDimensionIncludeChildren,
                ],
                globalFilterKeys: [
                    WidgetFilterKey.GlobalFilter.Period,
                    WidgetFilterKey.GlobalFilter.Organization,
                    WidgetFilterKey.GlobalFilter.OrganizationIncludeChildren,
                    WidgetFilterKey.GlobalFilter.BusinessDimension,
                    WidgetFilterKey.GlobalFilter.BusinessDimensionIncludeChildren,
                    WidgetFilterKey.GlobalFilter.FrameworkDimension,
                    WidgetFilterKey.GlobalFilter.FrameworkDimensionIncludeChildren,
                ],
            },
            "1300": {
                component: RisksPerCatalogueWidgetComponent,
                module: ModuleType.ORM,
                title: "dashboard.widgetCatalog.risksPerCatalogueTitle",
                description: "dashboard.widgetCatalog.risksPerCatalogueDescription",
                icon: "fas fa-book",
                right: "HasRiskAccess",
                showLoader: true,
                minCols: 3,
                minRows: 3,
                hasGlobalOpen: true,
                allowedChartTypes: [
                    DashboardGraphType.bar,
                    DashboardGraphType.column,
                    DashboardGraphType.pie,
                ],
                legendConfigurable: true,
                customConfig: {
                    type: DashboardWidgetType.RiskPerCatalogueWidget,
                    globalFilterEnabled: true,
                    graphType: DashboardGraphType.pie,
                    legendType: DashboardLegendType.iconlabel,
                },
                configurations: [
                    DashboardWidgetMapping._globalOrganizationFilter.Organization,
                    DashboardWidgetMapping._globalOrganizationFilter.OrganizationIncludeChildren,
                ],
                globalFilterKeys: [
                    WidgetFilterKey.GlobalFilter.Organization,
                    WidgetFilterKey.GlobalFilter.OrganizationIncludeChildren,
                ],
            },
            "2010": {
                component: KriWidgetComponent,
                module: ModuleType.KRI,
                title: "dashboard.widgetCatalog.kriWidgetTitle",
                description: "dashboard.widgetCatalog.kriWidgetDescription",
                icon: "fad fa-tachometer-alt",
                right: "HasKriAccess",
                showLoader: true,
                minRows: 3,
                minCols: 3,
                hasGlobalOpen: true,
                allowedChartTypes: [
                    DashboardGraphType.bar,
                    DashboardGraphType.column,
                    DashboardGraphType.pie,
                ],
                legendConfigurable: true,
                customConfig: {
                    type: DashboardWidgetType.KriWidget,
                    globalFilterEnabled: false,
                    graphType: DashboardGraphType.pie,
                    legendType: DashboardLegendType.iconlabel,
                },
                configurations: [
                    {
                        prettyName: "dashboard.widgetCatalog.kriWidgetTypeLabel",
                        fieldName: WidgetFilterKey.Kri.KriType,
                        fieldType: GenericListFieldType.SingleSelect,
                        configType: WidgetConfigType.DataFilter,
                        defaultValue: 0,
                        getDataMethod: () => {
                            const values = Object.keys(KriWidgetTypeDescription)
                                .filter((value) => isNaN(Number(value)) === false)
                                .map(
                                    (key) =>
                                        new IdNameCombination(+key, KriWidgetTypeDescription[key]),
                                );
                            return of(values);
                        },
                    },
                    DashboardWidgetMapping._globalOrganizationFilter.Organization,
                    DashboardWidgetMapping._globalOrganizationFilter.OrganizationIncludeChildren,
                    DashboardWidgetMapping._globalBusinessDimensionFilter.BusinessDimension,
                    DashboardWidgetMapping._globalBusinessDimensionFilter
                        .BusinessDimensionIncludeChildren,
                    DashboardWidgetMapping._globalFrameworkDimensionFilter.FrameworkDimension,
                    DashboardWidgetMapping._globalFrameworkDimensionFilter
                        .FrameworkDimensionIncludeChildren,
                ],
                globalFilterKeys: [
                    WidgetFilterKey.GlobalFilter.Organization,
                    WidgetFilterKey.GlobalFilter.OrganizationIncludeChildren,
                    WidgetFilterKey.GlobalFilter.BusinessDimension,
                    WidgetFilterKey.GlobalFilter.BusinessDimensionIncludeChildren,
                    WidgetFilterKey.GlobalFilter.FrameworkDimension,
                    WidgetFilterKey.GlobalFilter.FrameworkDimensionIncludeChildren
                ],
            },
            "2100": {
                component: KriDetailWidgetComponent,
                module: ModuleType.KRI,
                title: "dashboard.widgetCatalog.kriDetailWidgetTitle",
                description: "dashboard.widgetCatalog.kriDetailWidgetDescription",
                icon: "fad fa-chart-line",
                right: "HasKriAccess",
                showLoader: true,
                minRows: 3,
                minCols: 4,
                hasGlobalOpen: true,
                customConfig: {
                    type: DashboardWidgetType.KriDetailWidget,
                    globalFilterEnabled: false,
                },
                configurations: [
                    {
                        prettyName: "dashboard.widgetCatalog.kriDetailWidgetKriLabel",
                        fieldName: WidgetFilterKey.Kri.KriGuid,
                        fieldType: GenericListFieldType.SingleSelect,
                        configType: WidgetConfigType.DataFilter,
                        defaultValue: null,
                        getDataMethod: () => {
                            const ds = AppConstants.AppInjector.get(KriDataService);
                            const kris = ds.getNameFilterModels();

                            return kris;
                        },
                    },
                ],
                globalFilterKeys: [],
            },
            "3010": {
                component: EventWidgetComponent,
                module: ModuleType.LER,
                title: "dashboard.widgetCatalog.eventWidgetTitle",
                description: "dashboard.widgetCatalog.eventWidgetDescription",
                icon: "fad fa-bolt",
                right: "HasEventAccess",
                showLoader: true,
                minRows: 3,
                minCols: 3,
                hasGlobalOpen: true,
                allowedChartTypes: [
                    DashboardGraphType.bar,
                    DashboardGraphType.column,
                    DashboardGraphType.pie,
                ],
                customConfig: {
                    type: DashboardWidgetType.EventWidget,
                    globalFilterEnabled: false,
                    graphType: DashboardGraphType.bar,
                },
                configurations: [
                    DashboardWidgetMapping._globalOrganizationFilter.Organization,
                    DashboardWidgetMapping._globalOrganizationFilter.OrganizationIncludeChildren,
                    DashboardWidgetMapping._globalBusinessDimensionFilter.BusinessDimension,
                    DashboardWidgetMapping._globalBusinessDimensionFilter
                        .BusinessDimensionIncludeChildren,
                ],
                globalFilterKeys: [
                    WidgetFilterKey.GlobalFilter.Organization,
                    WidgetFilterKey.GlobalFilter.OrganizationIncludeChildren,
                    WidgetFilterKey.GlobalFilter.BusinessDimension,
                    WidgetFilterKey.GlobalFilter.BusinessDimensionIncludeChildren,
                ],
            },
            "3020": {
                component: IncidentWidgetComponent,
                module: ModuleType.LER,
                title: "dashboard.widgetCatalog.incidentWidgetTitle",
                description: "dashboard.widgetCatalog.incidentWidgetDescription",
                icon: "fad fa-bolt",
                right: "HasEventAccess",
                showLoader: true,
                minRows: 3,
                minCols: 3,
                hasGlobalOpen: true,
                allowedChartTypes: [
                    DashboardGraphType.bar,
                    DashboardGraphType.column,
                    DashboardGraphType.pie,
                ],
                customConfig: {
                    type: DashboardWidgetType.IncidentWidget,
                    globalFilterEnabled: false,
                    graphType: DashboardGraphType.bar,
                },
                configurations: [
                    {
                        prettyName: "dashboard.widgetCatalog.incidentWidgetStatusLabel",
                        fieldName: WidgetFilterKey.Incident.FilterIncidentStatus,
                        fieldType: GenericListFieldType.MultiSelect,
                        configType: WidgetConfigType.DataFilter,
                        defaultValue: [],
                        getDataMethod: () => {
                            const ds = AppConstants.AppInjector.get(IncidentStatusService);
                            const values = from(ds.getAll());
                            const mapped = values.pipe(
                                map((items) =>
                                    items.map((item) => {
                                        return <IdNameCombination>{
                                            ID: +item.id,
                                            Name: item.name,
                                        };
                                    }),
                                ),
                            );
                            return mapped;
                        },
                    },
                    {
                        prettyName: "dashboard.widgetCatalog.incidentWidgetTypeLabel",
                        fieldName: WidgetFilterKey.Incident.IncidentType,
                        fieldType: GenericListFieldType.MultiSelect,
                        configType: WidgetConfigType.DataFilter,
                        defaultValue: [],
                        getDataMethod: () => {
                            const ds = AppConstants.AppInjector.get(IncidentMapbackService);
                            const values = from(
                                ds.getStructureForMapback(FormStructureType.IncidentType),
                            );
                            const mapped = values.pipe(
                                map((items) =>
                                    items.map((item) => {
                                        return <IdNameCombination>{
                                            ID: +item.id,
                                            Name: item.name,
                                        };
                                    }),
                                ),
                            );
                            return mapped;
                        },
                    },
                    DashboardWidgetMapping._globalOrganizationFilter.Organization,
                    DashboardWidgetMapping._globalOrganizationFilter.OrganizationIncludeChildren,
                    DashboardWidgetMapping._globalBusinessDimensionFilter.BusinessDimension,
                    DashboardWidgetMapping._globalBusinessDimensionFilter
                        .BusinessDimensionIncludeChildren,
                    DashboardWidgetMapping._globalFrameworkDimensionFilter.FrameworkDimension,
                    DashboardWidgetMapping._globalFrameworkDimensionFilter
                        .FrameworkDimensionIncludeChildren,
                ],
                globalFilterKeys: [
                    WidgetFilterKey.GlobalFilter.Organization,
                    WidgetFilterKey.GlobalFilter.OrganizationIncludeChildren,
                    WidgetFilterKey.GlobalFilter.BusinessDimension,
                    WidgetFilterKey.GlobalFilter.BusinessDimensionIncludeChildren,
                    WidgetFilterKey.GlobalFilter.FrameworkDimension,
                    WidgetFilterKey.GlobalFilter.FrameworkDimensionIncludeChildren,
                ],
            },
            "4010": {
                component: ControlSimpleEffOverdueWidgetComponent,
                module: ModuleType.Controls,
                title: "dashboard.widgetCatalog.controlsOverdueTitle",
                description: "dashboard.widgetCatalog.controlsOverdueDescription",
                icon: "fas fa-shield-alt",
                right: "HasControlAccess",
                showLoader: true,
                minRows: 3,
                minCols: 3,
                hasGlobalOpen: true,
                allowedChartTypes: [
                    DashboardGraphType.bar,
                    DashboardGraphType.column,
                    DashboardGraphType.pie,
                ],
                customConfig: {
                    type: DashboardWidgetType.SimpleEffOverdue,
                    globalFilterEnabled: false,
                    graphType: DashboardGraphType.column,
                },
                configurations: [
                    {
                        prettyName: "dashboard.widgetCatalog.controlsOverdueEffOrDiLabel",
                        fieldName: WidgetFilterKey.ControlOverdue.EffOrDI,
                        fieldType: GenericListFieldType.SingleSelect,
                        configType: WidgetConfigType.DataFilter,
                        defaultValue: 0,
                        getDataMethod: () => {
                            const values = [
                                <IdNameCombination>{
                                    ID: 1,
                                    Name: "dashboard.widgetCatalog.controlsOverdueEffOption",
                                },
                                <IdNameCombination>{
                                    ID: 2,
                                    Name: "dashboard.widgetCatalog.controlsOverdueDiOption",
                                },
                            ];

                            return of(values);
                        },
                    },
                    DashboardWidgetMapping._globalOrganizationFilter.Organization,
                    DashboardWidgetMapping._globalOrganizationFilter.OrganizationIncludeChildren,
                    DashboardWidgetMapping._globalBusinessDimensionFilter.BusinessDimension,
                    DashboardWidgetMapping._globalBusinessDimensionFilter
                        .BusinessDimensionIncludeChildren,
                    DashboardWidgetMapping._globalFrameworkDimensionFilter.FrameworkDimension,
                    DashboardWidgetMapping._globalFrameworkDimensionFilter
                        .FrameworkDimensionIncludeChildren,
                ],
                globalFilterKeys: [
                    WidgetFilterKey.GlobalFilter.Organization,
                    WidgetFilterKey.GlobalFilter.OrganizationIncludeChildren,
                    WidgetFilterKey.GlobalFilter.BusinessDimension,
                    WidgetFilterKey.GlobalFilter.BusinessDimensionIncludeChildren,
                    WidgetFilterKey.GlobalFilter.FrameworkDimension,
                    WidgetFilterKey.GlobalFilter.FrameworkDimensionIncludeChildren,
                ],
            },
            "4020": {
                component: DesignImplementationWidgetComponent,
                module: ModuleType.Controls,
                title: "dashboard.widgetCatalog.designImplementationTitle",
                description: "dashboard.widgetCatalog.designImplementationDescription",
                icon: "fas fa-shield-alt",
                right: "HasControlAccess",
                showLoader: true,
                minRows: 3,
                minCols: 3,
                hasGlobalOpen: true,
                allowedChartTypes: [DashboardGraphType.bar, DashboardGraphType.column],
                legendConfigurable: false,
                customConfig: {
                    type: DashboardWidgetType.DesignImplementationScore,
                    globalFilterEnabled: false,
                    graphType: DashboardGraphType.column,
                    legendType: DashboardLegendType.iconlabel,
                },
                configurations: [
                    DashboardWidgetMapping._globalOrganizationFilter.Organization,
                    DashboardWidgetMapping._globalOrganizationFilter.OrganizationIncludeChildren,
                    DashboardWidgetMapping._globalBusinessDimensionFilter.BusinessDimension,
                    DashboardWidgetMapping._globalBusinessDimensionFilter
                        .BusinessDimensionIncludeChildren,
                    DashboardWidgetMapping._globalFrameworkDimensionFilter.FrameworkDimension,
                    DashboardWidgetMapping._globalFrameworkDimensionFilter
                        .FrameworkDimensionIncludeChildren,
                ],
                globalFilterKeys: [
                    WidgetFilterKey.GlobalFilter.Organization,
                    WidgetFilterKey.GlobalFilter.OrganizationIncludeChildren,
                    WidgetFilterKey.GlobalFilter.BusinessDimension,
                    WidgetFilterKey.GlobalFilter.BusinessDimensionIncludeChildren,
                    WidgetFilterKey.GlobalFilter.FrameworkDimension,
                    WidgetFilterKey.GlobalFilter.FrameworkDimensionIncludeChildren,
                ],
            },
            "4100": {
                component: EffectivenessOverdueWidgetComponent,
                module: ModuleType.Controls,
                title: "dashboard.widgetCatalog.effectivenessOverdueTitle",
                description: "dashboard.widgetCatalog.effectivenessOverdueDescription",
                icon: "fas fa-shield-alt",
                right: "HasControlAccess",
                showLoader: true,
                minRows: 3,
                minCols: 3,
                hasGlobalOpen: true,
                allowedChartTypes: [
                    DashboardGraphType.bar,
                    DashboardGraphType.column,
                    DashboardGraphType.pie,
                ],
                legendConfigurable: false,
                customConfig: {
                    type: DashboardWidgetType.EffectivenessOverdue,
                    globalFilterEnabled: false,
                    graphType: DashboardGraphType.column,
                    legendType: DashboardLegendType.iconlabel,
                },
                configurations: [
                    DashboardWidgetMapping._globalOrganizationFilter.Organization,
                    DashboardWidgetMapping._globalOrganizationFilter.OrganizationIncludeChildren,
                ],
                globalFilterKeys: [
                    WidgetFilterKey.GlobalFilter.Organization,
                    WidgetFilterKey.GlobalFilter.OrganizationIncludeChildren,
                ],
            },
            "4500": {
                component: ControlEffTestingWidgetComponent,
                module: ModuleType.Controls,
                title: "dashboard.widgetCatalog.controlsEffTestingTitle",
                description: "dashboard.widgetCatalog.controlsEffTestingDescription",
                icon: "fas fa-shield-alt",
                right: "HasControlAccess",
                showLoader: true,
                minRows: 3,
                minCols: 3,
                hasGlobalOpen: true,
                allowedChartTypes: [
                    DashboardGraphType.bar,
                    DashboardGraphType.column,
                    DashboardGraphType.pie,
                ],
                legendConfigurable: false,
                customConfig: {
                    type: DashboardWidgetType.EffectivenessTesting,
                    globalFilterEnabled: false,
                    graphType: DashboardGraphType.column,
                    legendType: DashboardLegendType.iconlabel,
                },
                configurations: [
                    DashboardWidgetMapping._globalOrganizationFilter.Organization,
                    DashboardWidgetMapping._globalOrganizationFilter.OrganizationIncludeChildren,
                    DashboardWidgetMapping._globalBusinessDimensionFilter.BusinessDimension,
                    DashboardWidgetMapping._globalBusinessDimensionFilter
                        .BusinessDimensionIncludeChildren,
                    DashboardWidgetMapping._globalFrameworkDimensionFilter.FrameworkDimension,
                    DashboardWidgetMapping._globalFrameworkDimensionFilter
                        .FrameworkDimensionIncludeChildren,
                ],
                globalFilterKeys: [
                    WidgetFilterKey.GlobalFilter.Organization,
                    WidgetFilterKey.GlobalFilter.OrganizationIncludeChildren,
                    WidgetFilterKey.GlobalFilter.BusinessDimension,
                    WidgetFilterKey.GlobalFilter.BusinessDimensionIncludeChildren,
                    WidgetFilterKey.GlobalFilter.FrameworkDimension,
                    WidgetFilterKey.GlobalFilter.FrameworkDimensionIncludeChildren,
                ],
            },
            "7010": {
                component: FormOverviewWidgetComponent,
                module: ModuleType.FRM,
                title: "dashboard.widgetCatalog.formOverviewTitle",
                description: "dashboard.widgetCatalog.formOverviewDescription",
                icon: "fad fa-file-alt",
                showLoader: true,
                minRows: 2,
                minCols: 2,
                customConfig: {
                    type: DashboardWidgetType.FormOverviewWidget,
                    globalFilterEnabled: false,
                },
                configurations: [
                    ...DashboardWidgetMapping._defaultQuickLinkFilters,
                    {
                        prettyName: "dashboard.widgetCatalog.formOverviewFormsLabel",
                        fieldName: WidgetFilterKey.Forms.FormIDs,
                        fieldType: GenericListFieldType.MultiSelect,
                        configType: WidgetConfigType.DataFilter,
                        defaultValue: [],
                        getDataMethod: () => {
                            const ds = AppConstants.AppInjector.get(FormsDataService);
                            return ds.getFormWidgetOverview(null).pipe(
                                map((forms) =>
                                    forms.map((f) => {
                                        return {
                                            ID: f.ID,
                                            Name: f.Title,
                                        };
                                    }),
                                ),
                            );
                        },
                    },
                    {
                        prettyName: "dashboard.widgetCatalog.formOverviewLinkToNewLabel",
                        description: "dashboard.widgetCatalog.formOverviewLinkToNewDescription",
                        fieldName: WidgetFilterKey.Forms.LinkToNewFormEntry,
                        fieldType: GenericListFieldType.CheckBox,
                        configType: WidgetConfigType.WidgetConfig,
                        defaultValue: true,
                    },
                ],
                globalFilterKeys: [],
            },
            "13010": {
                component: FindingReportWidgetComponent,
                module: ModuleType.FR,
                title: "dashboard.widgetCatalog.findingReportTitle",
                description: "dashboard.widgetCatalog.findingReportDescription",
                icon: "fad fa-clipboard-list",
                right: "HasFindingReportAccess",
                showLoader: true,
                minRows: 3,
                minCols: 3,
                hasGlobalOpen: true,
                legendConfigurable: false,
                allowedChartTypes: [
                    DashboardGraphType.bar,
                    DashboardGraphType.column,
                    DashboardGraphType.pie,
                ],
                customConfig: {
                    type: DashboardWidgetType.FindingsReportWidget,
                    globalFilterEnabled: false,
                    graphType: DashboardGraphType.column,
                    legendType: DashboardLegendType.iconlabel,
                },
                configurations: [
                    DashboardWidgetMapping._globalOrganizationFilter.Organization,
                    {
                        prettyName: "dashboard.widgetCatalog.findingReportTypeLabel",
                        fieldName: WidgetFilterKey.FindingsReport.ReportType,
                        fieldType: GenericListFieldType.SingleSelect,
                        configType: WidgetConfigType.DataFilter,
                        required: true,
                        defaultValue: null,
                        getDataMethod: () => {
                            const ds = AppConstants.AppInjector.get(FindingReportDataService);
                            const reportTypes = ds
                                .getReportTypes()
                                .pipe(
                                    map((x) =>
                                        x.map(
                                            (rt) =>
                                                <IdNameCombination>{ ID: rt.value, Name: rt.label },
                                        ),
                                    ),
                                );

                            return reportTypes;
                        },
                    },
                ],
                globalFilterKeys: [WidgetFilterKey.GlobalFilter.Organization],
            },
            "14010": {
                component: ProcessWidgetComponent,
                module: ModuleType.NAV,
                title: "dashboard.widgetCatalog.processWidgetTitle",
                description: "dashboard.widgetCatalog.processWidgetDescription",
                icon: "fas fa-sitemap",
                right: "HasProcessViewerAccess",
                showLoader: true,
                minRows: 3,
                minCols: 3,
                hasGlobalOpen: true,
                customConfig: {
                    type: DashboardWidgetType.ProcessWidget,
                    globalFilterEnabled: false,
                },
                configurations: [
                    {
                        prettyName: "dashboard.widgetCatalog.processWidgetLinkToLabel",
                        fieldName: WidgetFilterKey.ProcessLink.LinkTo,
                        fieldType: GenericListFieldType.SingleSelect,
                        configType: WidgetConfigType.WidgetConfig,
                        required: true,
                        defaultValue: ProcessWidgetLink.BusinessNavigator,
                        getDataMethod: () => {
                            const values = Object.keys(ProcessWidgetLinkDescription)
                                .filter((value) => isNaN(Number(value)) === false)
                                .map(
                                    (key) =>
                                        new IdNameCombination(
                                            +key,
                                            ProcessWidgetLinkDescription[key],
                                        ),
                                );
                            return of(values);
                        },
                    },
                    DashboardWidgetMapping._globalOrganizationFilter.Organization,
                    DashboardWidgetMapping._globalBusinessDimensionFilter.BusinessDimension,
                ],
                globalFilterKeys: [
                    WidgetFilterKey.GlobalFilter.Organization,
                    WidgetFilterKey.GlobalFilter.BusinessDimension,
                ],
            },
            "14100": {
                component: BusinessAssessmentWidgetComponent,
                module: ModuleType.NAV,
                title: "dashboard.widgetCatalog.businessAssessmentTitle",
                description: "dashboard.widgetCatalog.businessAssessmentDescription",
                icon: "fas fa-building",
                right: "HasBDManagementAccess",
                showLoader: true,
                minRows: 3,
                minCols: 3,
                hasGlobalOpen: true,
                workspacePresetModuleKey: [
                    ModuleType[ModuleType.NAV],
                    ModuleSubType[ModuleSubType.BusinessNavigator],
                ].join("-"),
                legendConfigurable: false,
                customConfig: {
                    type: DashboardWidgetType.BusinessAssessmentsWidget,
                    globalFilterEnabled: false,
                    graphType: DashboardGraphType.pie,
                    legendType: DashboardLegendType.iconlabel,
                },
                configurations: [
                    this._getWorkspacePresetList(
                        ModuleType[ModuleType.NAV],
                        ModuleSubType[ModuleSubType.BusinessNavigator],
                    ),
                    DashboardWidgetMapping._globalOrganizationFilter.Organization,
                    DashboardWidgetMapping._globalOrganizationFilter.OrganizationIncludeChildren,
                    DashboardWidgetMapping._globalBusinessDimensionFilter.BusinessDimension,
                    DashboardWidgetMapping._globalBusinessDimensionFilter
                        .BusinessDimensionIncludeChildren,
                ],
                globalFilterKeys: [
                    WidgetFilterKey.GlobalFilter.Organization,
                    WidgetFilterKey.GlobalFilter.BusinessDimension,
                ],
            },
            "15010": {
                component: MoiWidgetComponent,
                module: ModuleType.IM,
                title: "dashboard.widgetCatalog.moiWidgetTitle",
                description: "dashboard.widgetCatalog.moiWidgetDescription",
                icon: "fad fa-tasks",
                right: "HasMoiAccess",
                showLoader: true,
                minRows: 3,
                minCols: 3,
                hasGlobalOpen: true,
                allowedChartTypes: [DashboardGraphType.bar, DashboardGraphType.column],
                legendConfigurable: false,
                customConfig: {
                    type: DashboardWidgetType.MoiWidget,
                    globalFilterEnabled: false,
                    graphType: DashboardGraphType.bar,
                    legendType: DashboardLegendType.iconlabel,
                },
                configurations: [
                    DashboardWidgetMapping._globalOrganizationFilter.Organization,
                    DashboardWidgetMapping._globalOrganizationFilter.OrganizationIncludeChildren,
                    DashboardWidgetMapping._globalBusinessDimensionFilter.BusinessDimension,
                    DashboardWidgetMapping._globalBusinessDimensionFilter
                        .BusinessDimensionIncludeChildren,
                    {
                        prettyName: "dashboard.widgetCatalog.moiWidgetTypesLabel",
                        fieldName: WidgetFilterKey.Moi.MoiTypes,
                        fieldType: GenericListFieldType.MultiSelect,
                        configType: WidgetConfigType.DataFilter,
                        defaultValue: [],
                        getDataMethod: (): Observable<IdNameCombination[]> => of(getMoiTypes()),
                    },
                ],
                globalFilterKeys: [
                    WidgetFilterKey.GlobalFilter.Organization,
                    WidgetFilterKey.GlobalFilter.OrganizationIncludeChildren,
                    WidgetFilterKey.GlobalFilter.BusinessDimension,
                    WidgetFilterKey.GlobalFilter.BusinessDimensionIncludeChildren,
                ],
            },
            "18010": {
                component: ThirdPartyWidgetComponent,
                module: ModuleType.TP,
                title: "dashboard.widgetCatalog.thirdPartyTitle",
                description: "dashboard.widgetCatalog.thirdPartyDescription",
                icon: "fad fa-handshake",
                right: "HasThirdPartyAccess",
                showLoader: true,
                minRows: 3,
                minCols: 3,
                hasGlobalOpen: true,
                allowedChartTypes: [
                    DashboardGraphType.bar,
                    DashboardGraphType.column,
                    DashboardGraphType.pie,
                ],
                legendConfigurable: false,
                customConfig: {
                    type: DashboardWidgetType.ThirdParty,
                    globalFilterEnabled: false,
                    graphType: DashboardGraphType.column,
                    legendType: DashboardLegendType.iconlabel,
                },
                configurations: [
                    DashboardWidgetMapping._globalOrganizationFilter.Organization,
                    DashboardWidgetMapping._globalOrganizationFilter.OrganizationIncludeChildren,
                    DashboardWidgetMapping._globalBusinessDimensionFilter.BusinessDimension,
                    DashboardWidgetMapping._globalBusinessDimensionFilter
                        .BusinessDimensionIncludeChildren,
                ],
                globalFilterKeys: [
                    WidgetFilterKey.GlobalFilter.Organization,
                    WidgetFilterKey.GlobalFilter.OrganizationIncludeChildren,
                    WidgetFilterKey.GlobalFilter.BusinessDimension,
                    WidgetFilterKey.GlobalFilter.BusinessDimensionIncludeChildren,
                ],
            },
            "20010": {
                component: LinkWidgetComponent,
                module: ModuleType.COMMON,
                title: "dashboard.widgetCatalog.auditOverviewTitle",
                description: "dashboard.widgetCatalog.auditOverviewDescription",
                icon: "fad fa-file-search",
                right: "auditing.hasModuleAccess",
                showLoader: true,
                minCols: 2,
                minRows: 2,
                customConfig: {
                    type: DashboardWidgetType.AuditOverview,
                    globalFilterEnabled: false,
                },
                configurations: [...DashboardWidgetMapping._defaultQuickLinkFilters],
            },
        };
    }

    private static _getWorkspacePresetList(
        module: string,
        appendToModule?: string,
    ): WidgetConfigField {
        let moduleString = "" + module;
        if (appendToModule) {
            moduleString += "-" + appendToModule;
        }

        return {
            prettyName: "dashboard.widgetCatalog.workspacePresetLabel",
            description: "dashboard.widgetCatalog.workspacePresetDescription",
            fieldName: WidgetFilterKey.GlobalFilter.WorkspacePreset,
            fieldType: GenericListFieldType.SingleSelect,
            configType: WidgetConfigType.DataFilter,
            required: false,
            defaultValue: null,
            getDataMethod: () => {
                const ds = AppConstants.AppInjector.get(WorkspacePresetService);
                const presets = ds
                    .getPresets(moduleString)
                    .pipe(
                        map((configs) =>
                            configs.map(
                                (c) =>
                                    new IdNameCombination(
                                        c.id,
                                        c.name + (c.isPublic ? " (public)" : " (private)"),
                                    ),
                            ),
                        ),
                    );

                return presets;
            },
        };
    }

    private static _globalOrganizationFilter: {
        Organization: WidgetConfigField;
        OrganizationIncludeChildren: WidgetConfigField;
    } = {
        Organization: {
            prettyName: "dashboard.widgetCatalog.globalFilterOrganizationLabel",
            fieldName: WidgetFilterKey.GlobalFilter.Organization,
            fieldType: GenericListFieldType.SingleTree,
            configType: WidgetConfigType.DataFilter,
            defaultValue: null,
            getDataMethodByRow: (row, ds) => {
                const selectedOrgId = row ? row[WidgetFilterKey.GlobalFilter.Organization] : null;
                return ds.getOrganizationsIncludingSelected(ModuleType.DSB, selectedOrgId);
            },
        },
        OrganizationIncludeChildren: {
            prettyName: "dashboard.widgetCatalog.globalFilterOrgIncludeChildrenLabel",
            fieldName: WidgetFilterKey.GlobalFilter.OrganizationIncludeChildren,
            fieldType: GenericListFieldType.CheckBox,
            configType: WidgetConfigType.DataFilter,
            defaultValue: true,
            hideFillers: true,
        },
    };

    private static _globalBusinessDimensionFilter: {
        BusinessDimension: WidgetConfigField;
        BusinessDimensionIncludeChildren: WidgetConfigField;
    } = {
        BusinessDimension: {
            prettyName: "dashboard.widgetCatalog.globalFilterBusinessDimensionLabel",
            fieldName: WidgetFilterKey.GlobalFilter.BusinessDimension,
            fieldType: GenericListFieldType.SingleTree,
            configType: WidgetConfigType.DataFilter,
            defaultValue: null,
            getDataMethodByRow: (row, ds) => {
                let orgid = row ? row[WidgetFilterKey.GlobalFilter.Organization] : null;
                if (!orgid) {
                    orgid = -1;
                }

                const selectedBdId = row
                    ? row[WidgetFilterKey.GlobalFilter.BusinessDimension]
                    : null;
                return ds.getBusinessDimensionsByOrganization(orgid, selectedBdId);
            },
            checkReloadDataByRowChanges(previous, current) {
                const keyToCheck = WidgetFilterKey.GlobalFilter.Organization;
                if (previous[keyToCheck] !== current[keyToCheck]) {
                    return true;
                }

                return false;
            },
        },
        BusinessDimensionIncludeChildren: {
            prettyName: "dashboard.widgetCatalog.globalFilterBdIncludeChildrenLabel",
            fieldName: WidgetFilterKey.GlobalFilter.BusinessDimensionIncludeChildren,
            fieldType: GenericListFieldType.CheckBox,
            configType: WidgetConfigType.DataFilter,
            defaultValue: true,
            hideFillers: true,
        },
    };

    private static _globalFrameworkDimensionFilter: {
        FrameworkDimension: WidgetConfigField;
        FrameworkDimensionIncludeChildren: WidgetConfigField;
    } = {
        FrameworkDimension: {
            prettyName: "dashboard.widgetCatalog.globalFilterFrameworkDimensionLabel",
            fieldName: WidgetFilterKey.GlobalFilter.FrameworkDimension,
            fieldType: GenericListFieldType.SingleTree,
            configType: WidgetConfigType.DataFilter,
            defaultValue: null,
            getDataMethodByRow: (row, ds) => {
                let bdid = row ? row[WidgetFilterKey.GlobalFilter.BusinessDimension] : null;
                if (!bdid) {
                    bdid = -1;
                }

                const selectedFdId = row
                    ? row[WidgetFilterKey.GlobalFilter.FrameworkDimension]
                    : null;
                return ds.getFrameworkDimensions(bdid, selectedFdId);
            },
            checkReloadDataByRowChanges(previous, current) {
                const keyToCheck = WidgetFilterKey.GlobalFilter.BusinessDimension;
                if (previous[keyToCheck] !== current[keyToCheck]) {
                    return true;
                }

                return false;
            },
        },
        FrameworkDimensionIncludeChildren: {
            prettyName: "dashboard.widgetCatalog.globalFilterFdIncludeChildrenLabel",
            fieldName: WidgetFilterKey.GlobalFilter.FrameworkDimensionIncludeChildren,
            fieldType: GenericListFieldType.CheckBox,
            configType: WidgetConfigType.DataFilter,
            defaultValue: true,
            hideFillers: true,
        },
    };

    private static _globalPeriodFilter: WidgetConfigField = {
        prettyName: "dashboard.widgetCatalog.globalFilterPeriodLabel",
        fieldName: WidgetFilterKey.GlobalFilter.Period,
        fieldType: GenericListFieldType.SingleSelect,
        configType: WidgetConfigType.DataFilter,
        defaultValue: 0,
        getDataMethodByRow: (row: any, ds: StandingdataDataService) => {
            return ds.getPeriods(ModuleType.ORM);
        },
    };

    private static _defaultQuickLinkFilters: WidgetConfigField[] = [
        {
            prettyName: "dashboard.widgetCatalog.defaultQuickLinkFontSizeLabel",
            fieldName: WidgetFilterKey.QuickLink.FontSize,
            fieldType: GenericListFieldType.Number,
            configType: WidgetConfigType.WidgetConfig,
            defaultValue: 16,
            required: true,
        },
        {
            prettyName: "dashboard.widgetCatalog.defaultQuickLinkContentLabel",
            fieldName: WidgetFilterKey.QuickLink.LinkContent,
            fieldType: GenericListFieldType.SingleSelect,
            configType: WidgetConfigType.WidgetConfig,
            defaultValue: 10,
            required: true,
            getDataMethod: () => {
                const options: IdNameCombination[] = [
                    {
                        ID: QuickLinkItemContent.IconTitleDescription,
                        Name: "dashboard.widgetCatalog.quickLinkContentIconTitleDescriptionOption",
                    },
                    {
                        ID: QuickLinkItemContent.IconTitle,
                        Name: "dashboard.widgetCatalog.quickLinkContentIconTitleOption",
                    },
                    {
                        ID: QuickLinkItemContent.Icon,
                        Name: "dashboard.widgetCatalog.quickLinkContentIconOption",
                    },
                ];

                return of(options);
            },
        },
    ];
}
